import * as React from 'react';

import moment from 'moment-timezone';

import { currency } from '@shared/utils';
import { ALL_DAY_SLA_END, ALL_DAY_SLA_START, isAllDaySLA } from '@shared/utils/all_day_sla';

import { Spinner } from '@admin/components/spinner';

export interface IScheduleEvent {
  id: string;
  available: boolean;
  from_time: Date;
  till_time: Date;
  duration: { valueOf(): number };
  appointmentFee?: string | null;
  laborRate?: {
    id: string;
    amount: number;
  } | null;
  perMoverHourAdjustmentAmount?: number | null;
  laborBillingFormat?: string | null;
  laborCreditEligible?: boolean;
  disabled?: boolean;
}

interface IEventGroup {
  when: Date;
  events: IScheduleEvent[];
}

export interface IPerItemFee {
  amount: number;
  largeAmount: number;
}

interface IScheduleProps {
  events: IScheduleEvent[];
  dates: Date[];
  timezone: string;
  selectedEvent?: string;
  activeEvent?: string;
  className?: string;
  emptyDayMessage?: string;
  emptyMessage?: string;
  footerMessage?: string;
  untouchedMessage?: string;
  unlimited?: boolean;
  loading?: boolean;
  perItemFee?: IPerItemFee | null;
  onScheduleEventClick?(event: {
    id: string;
    duration: { valueOf(): number };
    laborRate?: IScheduleEvent['laborRate'];
    perMoverHourAdjustmentAmount?: number | null;
  }): void;
}

const sortDatesAsc = (eventGroup1: IEventGroup, eventGroup2: IEventGroup): number => {
  if (eventGroup1.when > eventGroup2.when) return 1;
  if (eventGroup1.when < eventGroup2.when) return -1;
  return 0;
};

const addEmptyDates = (dates: Date[], dateGroups: IEventGroup[], timezone: string): IEventGroup[] => {
  dates.forEach((d) => {
    const found = dateGroups.some((g) => moment.tz(d, timezone).isSame(moment.tz(g.when, timezone), 'day'));
    if (!found) {
      dateGroups.push({
        when: d,
        events: [],
      });
    }
  });

  return dateGroups.sort(sortDatesAsc);
};

export const Schedule: React.FC<IScheduleProps> = ({
  events = [],
  dates = [],
  timezone,
  selectedEvent,
  activeEvent,
  className,
  emptyDayMessage = 'No time slots available for this job.',
  emptyMessage,
  footerMessage,
  untouchedMessage,
  unlimited,
  loading,
  perItemFee,
  onScheduleEventClick,
}) => {
  const [touched, setTouched] = React.useState(false);

  React.useEffect(() => {
    if (events.length > 0) {
      setTouched(true);
    }
  }, [events]);

  const createUnlimitedGroupedEvents = React.useCallback(
    (d: Date): IEventGroup => ({
      when: d,
      events: moment.tz(d, timezone).isSame(moment.tz(new Date(), timezone), 'day')
        ? []
        : [
            {
              id: moment(d).tz(timezone).set({ hour: 7 }).toISOString(),
              available: true,
              from_time: moment(d).tz(timezone).set({ hour: 7 }).toDate(),
              till_time: moment(d).tz(timezone).set({ hour: 10 }).toDate(),
              duration: { valueOf: () => 3 * 60 * 60 * 1000 },
            },
            {
              id: moment(d).tz(timezone).set({ hour: 9 }).toISOString(),
              available: true,
              from_time: moment(d).tz(timezone).set({ hour: 9 }).toDate(),
              till_time: moment(d).tz(timezone).set({ hour: 12 }).toDate(),
              duration: { valueOf: () => 3 * 60 * 60 * 1000 },
            },
            {
              id: moment(d).tz(timezone).set({ hour: 11 }).toISOString(),
              available: true,
              from_time: moment(d).tz(timezone).set({ hour: 11 }).toDate(),
              till_time: moment(d).tz(timezone).set({ hour: 14 }).toDate(),
              duration: { valueOf: () => 3 * 60 * 60 * 1000 },
            },
          ],
    }),
    [timezone],
  );

  const createGroupedEvents = React.useCallback((): IEventGroup[] => {
    if (unlimited) {
      return [...dates].sort((a, b) => a.getTime() - b.getTime()).map(createUnlimitedGroupedEvents);
    }

    const sorted = [...events].sort((a, b) => {
      const fromDiff = a.from_time.getTime() - b.from_time.getTime();
      if (fromDiff !== 0) return fromDiff;
      return a.till_time.getTime() - b.till_time.getTime();
    });

    const groups: Map<number, IScheduleEvent[]> = new Map();
    sorted.forEach((event) => {
      const key = moment.tz(event.from_time, timezone).startOf('day').valueOf();
      const existing = groups.get(key) || [];
      groups.set(key, [...existing, event]);
    });

    const dateGroups: IEventGroup[] = Array.from(groups.entries())
      .sort(([a], [b]) => a - b)
      .map(([when, groupEvents]) => ({
        when: new Date(when),
        events: groupEvents,
      }));

    return addEmptyDates(dates, dateGroups, timezone);
  }, [dates, events, unlimited, createUnlimitedGroupedEvents, timezone]);

  const currentGroupedEvents = React.useMemo(() => createGroupedEvents(), [createGroupedEvents]);

  const eventDisabled = (id: string): boolean => {
    if (unlimited) return false;
    const event = events.find((e) => e.id === id);
    return !event || !!event.disabled;
  };

  const eventDescription = (event: IScheduleEvent): string => {
    const laborRateAmount = event.laborRate && currency(event.laborRate.amount);
    const laborDescription = event.laborCreditEligible
      ? 'Free Labor Eligible'
      : laborRateAmount && `Labor: ${laborRateAmount} ${event.laborBillingFormat}`;
    const appointmentFeeDescription =
      event.appointmentFee &&
      perItemFee &&
      `$${perItemFee.amount}/Item (1 mover) + $${perItemFee.largeAmount}/Item (2+ movers) + $${parseFloat(
        event.appointmentFee,
      ).toFixed(2)} Service Charge`;

    return [laborDescription, appointmentFeeDescription].filter(Boolean).join(', ');
  };

  const getCalendarClass = (id: string | undefined): string => {
    if (!id) {
      return '';
    }
    const disabled = eventDisabled(id);

    const classes: string[] = [];
    if (onScheduleEventClick && !disabled) {
      classes.push('calendar-event-clickable');
    } else if (disabled) {
      classes.push('calendar-disabled');
    }
    if (selectedEvent === id) {
      classes.push('selected');
    } else if (activeEvent === id) {
      classes.push('active');
    }
    return classes.join(' ');
  };

  const handleCalendarClick = (event: IScheduleEvent): void => {
    if (onScheduleEventClick && event.id && !eventDisabled(event.id)) {
      onScheduleEventClick({
        id: event.id,
        duration: event.duration,
        laborRate: event.laborRate,
        perMoverHourAdjustmentAmount: event.perMoverHourAdjustmentAmount,
      });
    }
  };

  const showUntouchedMessage = (): boolean => !loading && !touched;

  const allDaySLA = (): boolean => {
    return currentGroupedEvents.some(({ events: groupEvents }) =>
      groupEvents.some((event) => {
        const fromHour = moment.tz(event.from_time, timezone).hour();
        const tillHour = moment.tz(event.till_time, timezone).hour();
        return isAllDaySLA(fromHour, tillHour);
      }),
    );
  };

  const formatTime = (date: Date): string => {
    return moment.tz(date, timezone).format('h:mma');
  };

  const formatDate = (date: Date): string => {
    return moment.tz(date, timezone).format('MMM. D, YYYY');
  };

  const formatDayOfWeek = (date: Date): string => {
    return moment.tz(date, timezone).format('dddd');
  };

  return (
    <div className={`calendar-schedule-wrapper ${className || ''}`}>
      <ul className="calendar-schedule">
        <Spinner visible={loading} />
        {showUntouchedMessage() && (
          <li>
            <div>{untouchedMessage}</div>
          </li>
        )}
        {allDaySLA() && (
          <strong>
            <time className="calendar-dow">
              We are currently not providing arrival windows for small and medium sub-jobs at the time of booking. An
              arrival window will be emailed to the customer the day before their appointment. Arrival window will be
              between {ALL_DAY_SLA_START} and {ALL_DAY_SLA_END}.
            </time>
          </strong>
        )}
        {!showUntouchedMessage() &&
          !loading &&
          currentGroupedEvents.map((group, groupIndex) => (
            <li key={groupIndex} data-datetime={group.when.toISOString()}>
              <h3 className="col-sm-12 date-header">
                <time dateTime={group.when.toISOString()}>{formatDate(group.when)}</time>
                <time className="calendar-dow" dateTime={group.when.toISOString()}>
                  {formatDayOfWeek(group.when)}
                </time>
              </h3>
              {group.events.length > 0 ? (
                <ul className="calendar-schedule-group col-sm-12">
                  {group.events
                    .filter((event) => event.available)
                    .map((event) => (
                      <li
                        key={event.id}
                        className={getCalendarClass(event.id)}
                        role={onScheduleEventClick ? 'button' : undefined}
                        tabIndex={onScheduleEventClick ? 0 : -1}
                        onKeyDown={(e) => {
                          if ((e.key === 'Enter' || e.key === ' ') && !eventDisabled(event.id)) {
                            e.preventDefault();
                            handleCalendarClick(event);
                          }
                        }}
                        onClick={() => handleCalendarClick(event)}
                      >
                        <div>
                          <time dateTime={event.from_time.toISOString()}>{formatTime(event.from_time)}</time>
                          {' - '}
                          <time dateTime={event.till_time.toISOString()}>{formatTime(event.till_time)}</time>
                          <span className="pull-right text-bold">{eventDescription(event)}</span>
                        </div>
                      </li>
                    ))}
                </ul>
              ) : (
                <div className="col-sm-12 empty-events">{emptyDayMessage}</div>
              )}
              <span className="clearfix" />
            </li>
          ))}
        {!showUntouchedMessage() && currentGroupedEvents.length === 0 && (
          <li>
            <div>{emptyMessage}</div>
          </li>
        )}
      </ul>
      {footerMessage && <div className="footer-message">{footerMessage}</div>}
    </div>
  );
};
